import URI from 'urijs';
import { SEARCH_API_ROOT } from '../../../config';
import nonCriticalException from '../../exceptionLogger';
import { ssFetchJSON } from '../../ssFetch';
import { IQueryResult } from './types';
import ABTest from '../../abTest';
import { SEARCH_AUTOFILL_UPDATED_SUGGESTIONS_KEY, SEARCH_AUTOFILL_UPDATED_SUGGESTIONS_PERCENT } from './Constants';

const VERSION = '/api/v2.0';

const ENDPOINTS = {
  all: '/suggestions.json',
  services: '/suggestions/services.json',
  pros: '/suggestions/pros.json',
  salons: '/suggestions/salons.json',
};

const isEmpty = value => (value && typeof value === 'object' && Object.keys(value).length === 0);

/**
 * Parse the results to get exact/fuzzy suggestions based on type
 * @param results
 * @param type
 * @param {String} query The original query for the search.
 * @returns {Array} Suggestions of the given type
 */
function filterByResultsType(results, type, query) {
  const normalizedQuery = (query) ? String(query)
    .toLowerCase() : '';

  return results.map(result => {
    const text = (type === 'services') ? result.search_query.query : result.name;
    const idx = String(text)
      .toLowerCase()
      .indexOf(normalizedQuery);
    const matchedSubstrings = [];

    if (idx !== -1) {
      matchedSubstrings.push({
        offset: idx,
        length: query.length,
      });
    }

    return {
      ...result,
      type,
      matchedSubstrings,
    };
  });
}

/**
 * Transform the search results to a client-consumable format.
 *
 * @param {Object} results The results returned from the search.styleseat.com/.../suggestions.json
 *   endpoint.
 * @param {String} query The original query for the search.
 * @returns {Object} Arrays of all the unique results, under `services`, `salons`, and `pros` keys.
 */
function filterAndFormatResults(results, query) {
  return {
    services: filterByResultsType(results.services, 'services', query),
    salons: filterByResultsType(results.salons, 'salons', query),
    pros: filterByResultsType(results.pros, 'pros', query),
  };
}

/**
 * Fetches the autocomplete suggestions from the search API.
 *
 * @param {String} query The search term. Only the first 12 characters are used.
 * @param {String} [type='all'] Search suggestion type. Can be `services`, `pros`, `salons`, `all`
 * @yields {Object} Arrays of all the unique results, under `services`, `salons`, and `pros`
 * keys.
 */
export default async function fetchSuggestions(query: string, type: string = 'all'): Promise<IQueryResult> {
  // The search API returns a 400 for queries longer than 12 characters.
  const searchQuery = query.slice(0, 12);
  const emptyResult = {
    services: [],
    pros: [],
    salons: [],
  };
  const isInUpdatedAutofillSearchSuggestionsTest = await ABTest.isInFeatureTest(
    SEARCH_AUTOFILL_UPDATED_SUGGESTIONS_KEY,
    SEARCH_AUTOFILL_UPDATED_SUGGESTIONS_PERCENT,
  );
  const suggestionsUrl = URI(SEARCH_API_ROOT + VERSION + ENDPOINTS[type])
    .addQuery({
      query: searchQuery,
      updated_suggestions: isInUpdatedAutofillSearchSuggestionsTest,
    })
    .valueOf();

  try {
    const json = await ssFetchJSON<IQueryResult & { used_updated_suggestions?: boolean }>(
      suggestionsUrl,
      {
        // clear the headers because the search API gets confused with the ones we send by default
        headers: {},
      },
    );

    // if there's nothing in the results, just return the default
    if (isEmpty(json)) {
      return emptyResult;
    }
    // ex: if you are in the test group, but type in asdlfkjasldkfjadsf,
    // search suggestions will still not be returned.
    // ex 2: if you are NOT in the test group, but type 'Hair',
    // search suggestions *will* be returned

    // if the calling code specified a specific type to search for
    if (type !== 'all') {
      return {
        ...emptyResult,
        [type]: filterByResultsType(json, type, query),
      };
    }
    // transform the results to match expectations of calling code
    return filterAndFormatResults(json, query);
  } catch (err) {
    // we want to know about the error
    nonCriticalException(err, { searchUrl: suggestionsUrl });
    // but we don't want it to affect anything adversely
    return emptyResult;
  }
}
